<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  useStoreGetters,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

import WhatsAppCampaignResultsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignResultsDialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const resultsDialogRef = ref(null);
let statsPollTimer = null;

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleCreate = () => router.push({ name: 'campaigns_whatsapp_new' });

const handleEdit = campaign =>
  router.push({
    name: 'campaigns_whatsapp_edit',
    params: { campaignId: campaign.id },
  });

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleAnalytics = campaign => {
  router.push({
    name: 'campaigns_whatsapp_analytics',
    params: { campaignId: campaign.id },
  });
};
const handleResults = campaign => {
  selectedCampaign.value = campaign;
  resultsDialogRef.value?.dialogRef?.open();
};

const handleRepeat = campaign => {
  router.push({
    name: 'campaigns_whatsapp_new',
    query: { repeat: campaign.id },
  });
};

const refreshStats = async () => {
  const campaigns = WhatsAppCampaigns.value || [];
  const active = campaigns.filter(c => (c?.whatsapp_stats?.percent || 0) < 100);
  await Promise.all(
    active
      .slice(0, 10)
      .map(c => store.dispatch('campaigns/fetchWhatsAppCampaignStats', c.id))
  );
};

onMounted(() => {
  statsPollTimer = setInterval(refreshStats, 5000);
});

onBeforeUnmount(() => {
  if (statsPollTimer) clearInterval(statsPollTimer);
  statsPollTimer = null;
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="handleCreate"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      show-edit
      @edit="handleEdit"
      @delete="handleDelete"
      @analytics="handleAnalytics"
      @results="handleResults"
      @repeat="handleRepeat"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <WhatsAppCampaignResultsDialog
      ref="resultsDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
